import React, { useState, useEffect } from "react";
import { C, fmt, statusLabel, text } from "../theme";
import { usePermissions } from "../PermissionContext";
import WatcherPanel from "../components/WatcherPanel";
import DocumentSection from "../components/DocumentSection";

import { Card, Btn, Input, Notice, Select, DocLink, StatusPill as Badge, ApprovalTrail, ReturnedBanner, rowAction } from "../components/ui";
import { aorBudgetChip } from "../lib/budgetChip";
import { api } from "../api";

const BUSINESS_UNITS = ["NIC", "SSIR", "SMP", "TRO", "Corporate"];

export default function AORPage({ initialId, onOpenDoc }) {
  const { me } = usePermissions();
  const [aors, setAors] = useState([]);
  const [sel, setSel] = useState(initialId || null);
  const [showForm, setShowForm] = useState(false);
  const [budgets, setBudgets] = useState([]);
  const [loading, setLoading] = useState(true);
  // Admin-managed account codes (UX-17) — the dropdown used to be hard-coded
  // in the client, so Reference Data edits never reached this form.
  const [accountCodes, setAccountCodes] = useState([]);
  const [tenderThreshold, setTenderThreshold] = useState(null);
  useEffect(() => {
    api.getReferenceLookup("account-codes").then(a => setAccountCodes(Array.isArray(a) ? a.filter(x => x.isActive !== false) : [])).catch(() => setAccountCodes([]));
    api.getProcurementPolicy().then((p) => setTenderThreshold(p.tenderThreshold)).catch(() => {});
  }, []);

  // Form state
  const [form, setForm] = useState({
    description: "", businessUnit: "", budgetRef: "", noteToApprover: "",
    lineItems: [{ description: "", quantity: 1, unitPrice: 0, accountCode: "", budgetRef: "" }],
  });
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState(null); // { kind: "success" | "error", text }

  const load = () => {
    setLoading(true);
    Promise.all([
      fetch("/api/aors").then(r => r.json()),
      fetch("/api/budgets").then(r => r.json()).catch(() => []),
    ]).then(([p, b]) => {
      setAors(p);
      setBudgets(b);
    }).catch(console.error).finally(() => setLoading(false));
  };
  useEffect(load, []);

  // Compute form total
  const formTotal = form.lineItems.reduce((s, li) => s + (li.quantity * li.unitPrice), 0);

  // Filter budgets by selected business unit — the header filter also applies to
  // the per-line budget dropdown (UX-78), so a line can't silently fund from
  // another BU's budget while the header says otherwise.
  const filteredBudgets = budgets.filter(b => !form.businessUnit || b.bu === form.businessUnit);
  const headerBudget = budgets.find(b => b.ref === form.budgetRef);

  const updateLine = (idx, field, value) => {
    const lines = [...form.lineItems];
    lines[idx] = { ...lines[idx], [field]: field === "quantity" || field === "unitPrice" ? Number(value) : value };
    setForm({ ...form, lineItems: lines });
  };

  const addLine = () => {
    setForm({ ...form, lineItems: [...form.lineItems, { description: "", quantity: 1, unitPrice: 0, accountCode: "", budgetRef: "" }] });
  };

  const removeLine = (idx) => {
    if (form.lineItems.length === 1) return;
    setForm({ ...form, lineItems: form.lineItems.filter((_, i) => i !== idx) });
  };

  const handleCreate = async () => {
    if (!form.description || !form.businessUnit || !form.budgetRef) {
      setNotice({ kind: "error", text: "Please fill in Description, Business Unit, and Budget Reference." });
      return;
    }
    if (form.lineItems.some(li => !li.description || li.unitPrice <= 0)) {
      setNotice({ kind: "error", text: "Please fill in all line items with valid descriptions and prices." });
      return;
    }

    setSubmitting(true);
    try {
      // Get first user as requestor (in production, this comes from auth)
      const usersRes = await fetch("/api/aors");
      const existingAORs = await usersRes.json();
      // Use a simple approach - fetch users from an AOR or default
      let requestorId = existingAORs[0]?.requestorId;
      if (!requestorId) {
        requestorId = "system";
      }

      const res = await fetch("/api/aors", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          requestorId,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        // The form stays mounted with its input intact so the user can fix and retry.
        setNotice({ kind: "error", text: "Error: " + (data.error || "Failed to create AOR") });
        return;
      }
      setNotice({ kind: "success", text: "AOR Created: " + data.aorNumber + "\nBudget: " + (data.budgetCheck?.status || "OK") });
      setShowForm(false);
      setForm({ description: "", businessUnit: "", budgetRef: "", noteToApprover: "", lineItems: [{ description: "", quantity: 1, unitPrice: 0, accountCode: "", budgetRef: "" }] });
      load();
    } catch (err) {
      setNotice({ kind: "error", text: "Error: " + err.message });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitForApproval = async (aorId) => {
    try {
      const res = await fetch("/api/aors/" + aorId + "/submit", { method: "POST" });
      const data = await res.json();
      if (!res.ok) {
        setNotice({ kind: "error", text: "Error: " + (data.error || "Failed to submit") });
        return;
      }
      setNotice({ kind: "success", text: data.message });
      // Stay on the detail (UX-16): submit used to bounce to the list while
      // invoice submit stayed put — the detail shows the new status and trail.
      load();
    } catch (err) {
      setNotice({ kind: "error", text: "Error: " + err.message });
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: C.gray500 }}>Loading...</div>;

  // ─── Create Form ───
  if (showForm) {
    return (
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
          <Btn variant="outline" onClick={() => setShowForm(false)}>← Cancel</Btn>
          <h1 style={{ fontSize: text.h2, fontWeight: 700, margin: 0 }}>New Approval of Needs Request</h1>
        </div>

        <Card style={{ marginBottom: 16 }}>
          <div style={{ fontSize: text.lg, fontWeight: 700, marginBottom: 14 }}>Request Details</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px" }}>
            <Input label="Description *" placeholder="e.g. AWS Cloud Hosting - Annual" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
            <Select label="Business Unit *" value={form.businessUnit} onChange={e => {
              const bu = e.target.value;
              // Clear line budgets that no longer match the new BU (UX-78).
              const lines = form.lineItems.map(li => (li.budgetRef && budgets.find(b => b.ref === li.budgetRef)?.bu !== bu) ? { ...li, budgetRef: "" } : li);
              setForm({ ...form, businessUnit: bu, budgetRef: "", lineItems: lines });
            }} options={BUSINESS_UNITS.map(c => ({ value: c, label: c }))} />
            {/* Show availability where the spending decision is made (UX-46), not the raw annual. */}
            <Select label="Budget Reference *" value={form.budgetRef} onChange={e => setForm({ ...form, budgetRef: e.target.value })} options={filteredBudgets.map(b => ({ value: b.ref, label: b.ref + " — " + b.category + " (Avail: " + fmt(b.available) + ")" }))} />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label htmlFor="aor-note-to-approver" style={{ fontSize: text.sm, fontWeight: 600, color: C.gray700, display: "block", marginBottom: 4 }}>Note to Approver</label>
            <textarea id="aor-note-to-approver" value={form.noteToApprover} onChange={e => setForm({ ...form, noteToApprover: e.target.value })} placeholder="Justification or context for the approver..." style={{ width: "100%", padding: "8px 12px", border: `1px solid ${C.gray300}`, borderRadius: 6, fontSize: text.md, fontFamily: "inherit", minHeight: 60, resize: "vertical", boxSizing: "border-box" }} />
          </div>
        </Card>

        <Card style={{ marginBottom: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <div style={{ fontSize: text.lg, fontWeight: 700 }}>Line Items</div>
            <Btn variant="outline" onClick={addLine} style={{ padding: "4px 12px", fontSize: text.sm }}>+ Add Line</Btn>
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: text.md }}>
            <thead><tr>{["Description", "Qty", "Unit Price", "Account", "Budget", "Total", ""].map((h, i) => (
              <th scope="col" key={i} style={{ padding: "8px 10px", textAlign: "left", borderBottom: `2px solid ${C.gray200}`, color: C.gray600, fontWeight: 600, fontSize: text.xs, textTransform: "uppercase" }}>{h}</th>
            ))}</tr></thead>
            <tbody>
              {form.lineItems.map((li, idx) => (
                <tr key={idx} style={{ borderBottom: `1px solid ${C.gray100}` }}>
                  <td style={{ padding: "6px 8px" }}>
                    <input value={li.description} onChange={e => updateLine(idx, "description", e.target.value)} aria-label={`Line ${idx + 1} description`} placeholder="Item description" style={{ width: "100%", padding: "6px 8px", border: `1px solid ${C.gray300}`, borderRadius: 4, fontSize: text.md, fontFamily: "inherit", boxSizing: "border-box" }} />
                  </td>
                  <td style={{ padding: "6px 8px", width: 80 }}>
                    <input type="number" min="1" value={li.quantity} onChange={e => updateLine(idx, "quantity", e.target.value)} aria-label={`Line ${idx + 1} qty`} style={{ width: "100%", padding: "6px 8px", border: `1px solid ${C.gray300}`, borderRadius: 4, fontSize: text.md, fontFamily: "inherit", boxSizing: "border-box" }} />
                  </td>
                  <td style={{ padding: "6px 8px", width: 120 }}>
                    <input type="number" min="0" step="0.01" value={li.unitPrice} onChange={e => updateLine(idx, "unitPrice", e.target.value)} aria-label={`Line ${idx + 1} unit price`} style={{ width: "100%", padding: "6px 8px", border: `1px solid ${C.gray300}`, borderRadius: 4, fontSize: text.md, fontFamily: "inherit", boxSizing: "border-box" }} />
                  </td>
                  <td style={{ padding: "6px 8px", width: 180 }}>
                    <select value={li.accountCode} onChange={e => updateLine(idx, "accountCode", e.target.value)} aria-label={`Line ${idx + 1} account`} style={{ width: "100%", padding: "6px 8px", border: `1px solid ${C.gray300}`, borderRadius: 4, fontSize: text.md, fontFamily: "inherit", background: C.white, boxSizing: "border-box" }}>
                      <option value="">{accountCodes.length ? "Select account" : "No account codes configured"}</option>
                      {accountCodes.map((a) => <option key={a.id} value={a.code}>{a.code} - {a.label}</option>)}
                      {/* Keep a legacy code selectable when editing data created before the reference list applied. */}
                      {li.accountCode && !accountCodes.some(a => a.code === li.accountCode) && <option value={li.accountCode}>{li.accountCode} (legacy)</option>}
                    </select>
                  </td>
                  <td style={{ padding: "6px 8px", width: 180 }}>
                    <select value={li.budgetRef || ""} onChange={e => updateLine(idx, "budgetRef", e.target.value)} aria-label={`Line ${idx + 1} budget`} style={{ width: "100%", padding: "6px 8px", border: `1px solid ${C.gray300}`, borderRadius: 4, fontSize: text.md, fontFamily: "inherit", background: C.white, boxSizing: "border-box" }}>
                      <option value="">{form.budgetRef ? `— header budget (${form.budgetRef}) —` : "— budget —"}</option>
                      {filteredBudgets.map(b => <option key={b.ref} value={b.ref}>{b.ref} ({b.bu})</option>)}
                    </select>
                  </td>
                  <td style={{ padding: "6px 8px", width: 100, fontWeight: 700 }}>{fmt(li.quantity * li.unitPrice)}</td>
                  <td style={{ padding: "6px 8px", width: 40 }}>
                    {form.lineItems.length > 1 && (
                      <button type="button" onClick={() => removeLine(idx)} aria-label={`Remove line ${idx + 1}`} style={{ background: "none", border: "none", color: C.danger, cursor: "pointer", fontSize: text.h3 }}>×</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {(() => {
            const byRef = {};
            form.lineItems.forEach(l => { if (l.budgetRef) byRef[l.budgetRef] = (byRef[l.budgetRef] || 0) + (Number(l.quantity) || 0) * (Number(l.unitPrice) || 0); });
            const bus = [...new Set(form.lineItems.map(l => (budgets.find(b => b.ref === l.budgetRef) || {}).bu).filter(Boolean))];
            return (
              <div style={{ fontSize: text.sm, color: C.gray700, marginTop: 8 }}>
                <strong>Funding:</strong> {Object.entries(byRef).map(([r, amt]) => `${r}: $${amt.toLocaleString()}`).join(" · ") || "—"}
                {bus.length > 1 && <span> · <strong>BUs:</strong> {bus.join(", ")}</span>}
              </div>
            );
          })()}
          <div style={{ textAlign: "right", marginTop: 12, fontSize: text.h3, fontWeight: 700, color: C.gray1100 }}>
            Total: {fmt(formTotal)}
          </div>
          {/* Tender-threshold nudge at the moment of request (UX-10). */}
          {tenderThreshold != null && formTotal >= tenderThreshold && (
            <div style={{ marginTop: 10, padding: "10px 14px", borderRadius: 8, background: C.warningBg, fontSize: text.sm, color: C.gray700, lineHeight: 1.5 }}>
              <strong style={{ color: C.warning }}>⚠ Tender threshold:</strong> this request is at or above {fmt(tenderThreshold)}.
              After approval it should go to tender (Tender Requests → ITQ/ITT → Vendor Evaluation) rather than straight to a PO.
            </div>
          )}
        </Card>

        {/* Budget impact at the moment of decision (UX-46): the same shared
            availability figure every other surface shows. */}
        {headerBudget && (
          <Card style={{ marginBottom: 16, border: `2px solid ${(headerBudget.available - formTotal) < 0 ? C.danger : C.success}30` }}>
            <div style={{ fontSize: text.md, fontWeight: 700, marginBottom: 10 }}>Budget Impact — {headerBudget.ref}</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <div><div style={{ fontSize: text.xs, color: C.gray500 }}>Available</div><div style={{ fontSize: text.h2, fontWeight: 700 }}>{fmt(headerBudget.available)}</div></div>
              <div><div style={{ fontSize: text.xs, color: C.gray500 }}>This AOR</div><div style={{ fontSize: text.h2, fontWeight: 700 }}>{fmt(formTotal)}</div></div>
              <div><div style={{ fontSize: text.xs, color: C.gray500 }}>After</div><div style={{ fontSize: text.h2, fontWeight: 700, color: (headerBudget.available - formTotal) < 0 ? C.danger : C.success }}>{fmt(headerBudget.available - formTotal)}</div></div>
            </div>
          </Card>
        )}

        {/* Anchored to the action row so errors appear where the user acted. */}
        <Notice notice={notice} onDismiss={() => setNotice(null)} />
        <div style={{ display: "flex", gap: 10 }}>
          <Btn onClick={handleCreate} disabled={submitting}>{submitting ? "Creating..." : "Create AOR"}</Btn>
          <Btn variant="outline" onClick={() => setShowForm(false)}>Cancel</Btn>
        </div>
      </div>
    );
  }

  // ─── Detail View ───
  // A sel the loaded list doesn't contain (deep link to a document the user's
  // data scope hides, or a stale id) falls through to the list view.
  const selAor = sel ? aors.find(p => p.id === sel) : null;
  if (selAor) {
    const aor = selAor;
    const bc = aor.budgetCheck;
    // One check per budget line the AOR draws on (split funding), each charged
    // its own share; `bc` is the tightest of them and sets the overall status.
    const bcLines = aor.budgetChecks && aor.budgetChecks.length ? aor.budgetChecks : (bc ? [bc] : []);
    const shortLines = bcLines.filter(c => c.status === "OVER_BUDGET");
    const isOver = bc?.status === "OVER_BUDGET";
    const isNear = bc?.status === "NEAR_LIMIT";
    const bcColor = isOver ? C.danger : isNear ? C.warning : C.success;
    const bcBg = isOver ? C.dangerBg : isNear ? C.warningBg : C.successBg;
    const canSubmit = (aor.status === "DRAFT" || aor.status === "RETURNED") && !isOver;

    return (
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
          <Btn variant="outline" onClick={() => setSel(null)} style={{ padding: "4px 10px", fontSize: text.xs }}>← Back</Btn>
          <h1 style={{ fontSize: text.h2, fontWeight: 700, margin: 0 }}>{aor.aorNumber}</h1>
          <Badge status={aor.status} />
          <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
            {canSubmit && <Btn variant="success" onClick={() => handleSubmitForApproval(aor.id)}>Submit for Approval</Btn>}
            {/* Tender Requests are raised from the dedicated Tender Requests page (approved-AOR picker). */}
          </div>
        </div>

        <Notice notice={notice} onDismiss={() => setNotice(null)} />

        <ReturnedBanner status={aor.status} approvals={aor.approvals} />

        {/* Tender-threshold nudge on the approved AOR (UX-10): say which path
            comes next before the user reaches for a direct PO. */}
        {tenderThreshold != null && aor.status === "APPROVED" && aor.total >= tenderThreshold && !aor.tenderRequest && (aor.pos || []).length === 0 && (
          <Card style={{ marginBottom: 16, background: C.warningBg, border: `1px solid ${C.warningLight}` }}>
            <div style={{ fontSize: text.md, fontWeight: 700, color: C.warning, marginBottom: 4 }}>⚠ Next step: tender, not a direct PO</div>
            <div style={{ fontSize: text.sm, color: C.gray700, lineHeight: 1.5 }}>
              {fmt(aor.total)} is at or above the {fmt(tenderThreshold)} tender threshold. Raise a <b>Tender Request</b> from this AOR
              (Tender Requests page → New Tender Request), run the vendor evaluation, and create the PO from the award.
            </div>
          </Card>
        )}

        {/* Downstream chain (UX-49): where this AOR went after approval. */}
        {(aor.tenderRequest || aor.vendorEvaluation || (aor.pos || []).length > 0) && (
          <Card style={{ marginBottom: 16 }}>
            <div style={{ fontSize: text.lg, fontWeight: 700, marginBottom: 8 }}>Downstream documents</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 24px", fontSize: text.md }}>
              {aor.tenderRequest && (
                <div><span style={{ color: C.gray500 }}>Tender:</span> {onOpenDoc ? <DocLink onClick={() => onOpenDoc("tender-requests", aor.tenderRequest.id)}>{aor.tenderRequest.itqNumber || aor.tenderRequest.requestNumber} ({statusLabel(aor.tenderRequest.status)})</DocLink> : <strong>{aor.tenderRequest.itqNumber || aor.tenderRequest.requestNumber}</strong>}</div>
              )}
              {aor.vendorEvaluation && (
                <div><span style={{ color: C.gray500 }}>Vendor evaluation:</span> {onOpenDoc ? <DocLink onClick={() => onOpenDoc("vendor-eval", aor.vendorEvaluation.id)}>{aor.vendorEvaluation.evaluationNumber} ({statusLabel(aor.vendorEvaluation.status)})</DocLink> : <strong>{aor.vendorEvaluation.evaluationNumber}</strong>}</div>
              )}
              {(aor.pos || []).map(po => (
                <div key={po.id}><span style={{ color: C.gray500 }}>PO:</span> {onOpenDoc ? <DocLink onClick={() => onOpenDoc("po", po.id)}>{po.poNumber} · {po.vendor} · {fmt(po.total)} ({statusLabel(po.status)})</DocLink> : <strong>{po.poNumber}</strong>}</div>
              ))}
            </div>
          </Card>
        )}

        {aor.status === "PENDING_VIREMENT" && (
          <Card style={{ marginBottom: 16, border: `1px solid ${C.warningLight}`, background: C.warningBg }}>
            <div style={{ fontSize: text.md, fontWeight: 700, color: C.warning, marginBottom: 4 }}>⚠ Waiting on a budget virement</div>
            <div style={{ fontSize: text.sm, color: C.gray700, lineHeight: 1.5 }}>
              {shortLines.length
                ? <>This request exceeds the available budget in {shortLines.map((c, i) => <React.Fragment key={c.budgetRef}>{i > 0 && " and "}<b>{c.budgetRef}</b> by <b>{fmt(Math.abs(c.after || 0))}</b></React.Fragment>)}. </>
                : <>This request exceeds the available budget in <b>{aor.budgetRef}</b>. </>}
              {aor.linkedVirementId
                ? <>A virement is linked: once it is approved, this AOR returns to Draft for resubmission. If it is returned instead, the link is cleared and this AOR comes back here, to be covered by a new virement or trimmed to fit the budget.</>
                : <>To unblock it, create a virement on the <b>Virements</b> page and pick this AOR under "Linked AOR" (or in the over-budget panel), or trim the lines until they fit the budget. When that virement is approved, this AOR returns to Draft for resubmission.</>}
            </div>
          </Card>
        )}

        <Card style={{ marginBottom: 16 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, fontSize: text.md }}>
            <div><span style={{ color: C.gray500 }}>Requestor:</span> <strong>{aor.requestor?.displayName}</strong></div>
            <div><span style={{ color: C.gray500 }}>Business Unit:</span> <strong>{aor.businessUnit}</strong></div>
            <div><span style={{ color: C.gray500 }}>Budget Ref:</span> <strong style={{ color: C.primary }}>{aor.budgetRef}</strong></div>
            <div><span style={{ color: C.gray500 }}>Total:</span> <strong style={{ fontSize: text.h3 }}>{fmt(aor.total)}</strong></div>
            <div><span style={{ color: C.gray500 }}>Date:</span> <strong>{new Date(aor.createdAt).toLocaleDateString()}</strong></div>
          </div>
        </Card>

        {bc && (
          <Card style={{ padding: 0, marginBottom: 16, border: `2px solid ${bcColor}30`, overflow: "hidden" }}>
            <div style={{ padding: "12px 18px", background: bcBg, display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: text.h3 }}>{isOver ? "⚠️" : "✅"}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: text.md, fontWeight: 700, color: bcColor }}>Budget Check: {statusLabel(bc.status)}</div>
                {bcLines.length > 1 && <div style={{ fontSize: text.xs, color: C.gray600 }}>Split across {bcLines.length} budget lines — each line is charged only its own share.</div>}
              </div>
              <Badge status={bc.status} />
            </div>
            {bcLines.length <= 1 ? (
              <div style={{ padding: "14px 18px", display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                <div><div style={{ fontSize: text.xs, color: C.gray500 }}>Available (excl. this AOR)</div><div style={{ fontSize: text.h2, fontWeight: 700 }}>{fmt(bc.available)}</div></div>
                <div><div style={{ fontSize: text.xs, color: C.gray500 }}>This AOR</div><div style={{ fontSize: text.h2, fontWeight: 700 }}>{fmt(aor.total)}</div></div>
                <div><div style={{ fontSize: text.xs, color: C.gray500 }}>After</div><div style={{ fontSize: text.h2, fontWeight: 700, color: bc.after < 0 ? C.danger : C.success }}>{fmt(bc.after)}</div></div>
              </div>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: text.md }}>
                <thead><tr>{["Budget line", "Available (excl. this AOR)", "This AOR", "After", "Status"].map((h, i) => (
                  <th scope="col" key={i} style={{ padding: "10px 18px", textAlign: i === 0 ? "left" : "right", borderBottom: `1px solid ${C.gray200}`, color: C.gray600, fontWeight: 600, fontSize: text.xs, textTransform: "uppercase" }}>{h}</th>
                ))}</tr></thead>
                <tbody>
                  {bcLines.map(c => (
                    <tr key={c.budgetRef} style={{ borderBottom: `1px solid ${C.gray100}` }}>
                      <td style={{ padding: "10px 18px", fontWeight: 600, color: C.primary }}>{c.budgetRef}{c.bu ? <span style={{ fontWeight: 400, color: C.gray500 }}> · {c.bu}</span> : null}</td>
                      <td style={{ padding: "10px 18px", textAlign: "right" }}>{fmt(c.available)}</td>
                      <td style={{ padding: "10px 18px", textAlign: "right" }}>{fmt(c.charge)}</td>
                      <td style={{ padding: "10px 18px", textAlign: "right", fontWeight: 700, color: c.after < 0 ? C.danger : C.success }}>{fmt(c.after)}</td>
                      <td style={{ padding: "10px 18px", textAlign: "right" }}><Badge status={c.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        )}

        <Card style={{ marginBottom: 16 }}>
          <div style={{ fontSize: text.lg, fontWeight: 700, marginBottom: 10 }}>Line Items</div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: text.md }}>
            <thead><tr>{["Description", "Qty", "Unit Price", "Account", "Total"].map((h, i) => (
              <th scope="col" key={i} style={{ padding: "10px 12px", textAlign: "left", borderBottom: `2px solid ${C.gray200}`, color: C.gray600, fontWeight: 600, fontSize: text.xs, textTransform: "uppercase" }}>{h}</th>
            ))}</tr></thead>
            <tbody>
              {aor.lineItems?.map((li, i) => (
                <tr key={i} style={{ borderBottom: `1px solid ${C.gray100}` }}>
                  <td style={{ padding: "10px 12px" }}>{li.description}</td>
                  <td style={{ padding: "10px 12px" }}>{li.quantity}</td>
                  <td style={{ padding: "10px 12px" }}>{fmt(li.unitPrice)}</td>
                  <td style={{ padding: "10px 12px", color: C.gray600 }}>{li.accountCode || "—"}</td>
                  <td style={{ padding: "10px 12px", fontWeight: 600 }}>{fmt(li.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>

        {aor.noteToApprover && (
          <Card style={{ marginBottom: 16 }}>
            <div style={{ fontSize: text.lg, fontWeight: 700, marginBottom: 8 }}>Note to Approver</div>
            <div style={{ padding: 14, background: C.gray50, borderRadius: 8, fontSize: text.md, lineHeight: 1.6 }}>{aor.noteToApprover}</div>
          </Card>
        )}

        <ApprovalTrail approvals={aor.approvals} docType="AOR" style={{ marginBottom: 0 }} />

        <Card style={{ marginTop: 16 }}>
          <DocumentSection
            entityType="aor"
            entityId={aor.id}
            documents={aor.documents || []}
            checklistItems={aor.checklist || []}
            onChange={load}
          />
        </Card>

        <WatcherPanel docType="AOR" docId={aor.id} canManage={!!me && (me.id === aor.requestorId || me.roleName === "Administrator")} />
      </div>
    );
  }

  // ─── List View ───
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: text.h1, fontWeight: 700, color: C.gray1100, margin: 0 }}>Approval of Needs Requests</h1>
          <p style={{ fontSize: text.md, color: C.gray500, margin: "2px 0 0" }}>{aors.length} records</p>
        </div>
        <Btn onClick={() => { setNotice(null); setShowForm(true); }}>+ New AOR</Btn>
      </div>
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <Card style={{ padding: 0, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: text.md }}>
          <thead><tr>{["AOR No.", "Date", "Requestor", "Business Unit", "Budget Ref", "Total", "Budget", "Status"].map((h, i) => (
            <th scope="col" key={i} style={{ padding: "10px 12px", textAlign: "left", borderBottom: `2px solid ${C.gray200}`, color: C.gray600, fontWeight: 600, fontSize: text.xs, textTransform: "uppercase" }}>{h}</th>
          ))}</tr></thead>
          <tbody>
            {aors.length === 0 && (
              <tr><td colSpan={8} style={{ padding: 30, textAlign: "center", color: C.gray500 }}>
                No AORs yet — press + New AOR to raise a request for approval of needs.
              </td></tr>
            )}
            {aors.map(aor => (
              <tr key={aor.id} {...rowAction(() => { setNotice(null); setSel(aor.id); })} style={{ cursor: "pointer", borderBottom: `1px solid ${C.gray100}` }}
                onMouseEnter={e => e.currentTarget.style.background = C.gray50}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                <td style={{ padding: "10px 12px", fontWeight: 600, color: C.primary }}>{aor.aorNumber}</td>
                <td style={{ padding: "10px 12px" }}>{new Date(aor.createdAt).toLocaleDateString()}</td>
                <td style={{ padding: "10px 12px" }}>{aor.requestor?.displayName}</td>
                <td style={{ padding: "10px 12px" }}>{aor.businessUnit}</td>
                <td style={{ padding: "10px 12px", fontSize: text.xs, color: C.primary }}>{aor.budgetRef}</td>
                <td style={{ padding: "10px 12px" }}>{fmt(aor.total)}</td>
                <td style={{ padding: "10px 12px" }}>{(() => { const chip = aorBudgetChip(aor); return <span title={chip.title}><Badge status={chip.status} /></span>; })()}</td>
                <td style={{ padding: "10px 12px" }}><Badge status={aor.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
